import { forwardRef, useCallback, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { useForm, useFormState } from 'react-final-form';

import {
  Button,
  DrawerProps,
  useDrawer,
  useDrawerInstance,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  Typography,
} from '@cast/design-system';
import { DateRange } from '@cast/shared-types';
import { makeValidateSync } from '@cast/utils';

import { CloseGuardOverlay } from 'features/cost-report/components/CloseGuardOverlay';

import { Form } from './_components/Form';
import { LabelGroup } from '../../_types';
import {
  LabelsFilterSchema,
  labelsFilterSchema,
} from '../../validation.schema';

import { RffForm } from '@cast/shared-rff-ui';

const validate = makeValidateSync(labelsFilterSchema);

type BaseProps = {
  range: DateRange;
  selectedLabels: LabelGroup[];
  labelsSelected: (labels?: LabelGroup[]) => void;
};

type Props = BaseProps & DrawerProps;

const LabelsFilterFooter = () => {
  const { change, resetFieldState } = useForm();
  const { valid, values } = useFormState<LabelsFilterSchema>();

  const handleClearAll = useCallback(() => {
    change('labels', [{ key: '', value: [] }]);
    resetFieldState('labels');
  }, [change, resetFieldState]);

  return (
    <DrawerFooter>
      {!!values?.labels?.[0]?.key && (
        <Button variant="ghost" size="md" onClick={handleClearAll}>
          Clear all
        </Button>
      )}
      <Button type="submit" variant="primary" size="md" disabled={!valid}>
        Filter
      </Button>
    </DrawerFooter>
  );
};

const LabelsFilterDrawer = forwardRef<HTMLDivElement, Props>(
  ({ range, selectedLabels, labelsSelected, ...drawerProps }, ref) => {
    const { setOnCloseGuard, close, shouldRenderGuard, setShouldRenderGuard } =
      useDrawerInstance();
    const [valid, setValid] = useState(false);

    const handleSubmit = useCallback(
      (values: LabelsFilterSchema) => {
        if (valid && values) {
          labelsSelected(values.labels as LabelGroup[]);
          close();
        }
      },
      [valid, labelsSelected, close]
    );

    return (
      <Drawer
        ref={ref}
        size="small"
        testId="labels-filter-drawer"
        {...drawerProps}
      >
        <DrawerHeader>
          <Typography variant="h5">Filter by labels</Typography>
        </DrawerHeader>
        <RffForm
          onSubmit={handleSubmit}
          initialValues={{ labels: selectedLabels }}
          initialValuesEqual={isEqual}
          mode="create"
          validate={validate}
          onDirty={(isDirty) => {
            if (shouldRenderGuard !== isDirty) {
              setOnCloseGuard(isDirty);
            }
          }}
          onValidate={(isValid) => {
            setValid(isValid);
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'hidden',
          }}
          array
        >
          <DrawerBody>
            <DrawerContent>
              <Form range={range} />
              {shouldRenderGuard && (
                <CloseGuardOverlay
                  onClose={() => close()}
                  onContinue={() => setShouldRenderGuard(false)}
                />
              )}
            </DrawerContent>
          </DrawerBody>
          <LabelsFilterFooter />
        </RffForm>
      </Drawer>
    );
  }
);

LabelsFilterDrawer.displayName = 'LabelsFilterDrawer';

export const useLabelsFilterDrawer = ({
  onClose,
  ...rest
}: Omit<Props, 'selectedLabels'>) => {
  const { open } = useDrawer();

  const openDrawer = (
    selectedLabels: LabelGroup[] = [{ key: '', value: [] }]
  ) => {
    open({
      component: LabelsFilterDrawer,
      props: {
        onClose,
        selectedLabels,
        ...rest,
      },
    });
  };

  return {
    openDrawer,
  };
};
